import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { deleteOrder } from "../../../../../services/Admin/OrdersApi";
import { useAuth } from "../../../../../Context/AuthContext";

const DeleteOrderDialog = ({
  open,
  onClose,
  order,
  onDeleted,
  canDeleteOrder = true,
  blockedMessage = "ليس لديك صلاحية حذف هذا الطلب.",
  orderSubTotalBeforeFees,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const { userType, userRole } = useAuth();
  const isManagerAdmin = userType === "admin" && userRole === "manager";
  const isDeleteAllowed = canDeleteOrder && !isManagerAdmin;

  const handleDelete = async () => {
    if (!isDeleteAllowed) {
      setError(isManagerAdmin ? "ليس لديك صلاحية حذف هذا الطلب." : blockedMessage);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await deleteOrder(order.id);

      setSuccess(true);

      // إغلاق الديالوج بعد 1.5 ثانية
      setTimeout(() => {
        onDeleted && onDeleted(response);
        onClose();
      }, 1500);
    } catch (err) {
      console.error("خطأ في حذف الطلب:", err);
      setError(err.message || "حدث خطأ في حذف الطلب");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      dir="rtl"
    >
      <DialogTitle sx={{ fontWeight: "bold", textAlign: "right" }}>
        حذف الطلب
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            تم حذف الطلب بنجاح
          </Alert>
        )}

        {/* Warning Box */}
        {!success && (
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <WarningIcon
                sx={{
                  color: "#ff9800",
                  mt: 0.5,
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#856404",
                    fontWeight: "bold",
                    mb: 1,
                  }}
                >
                  تحذير: سيتم حذف الطلب نهائياً
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#856404",
                  }}
                >
                  هل أنت متأكد من رغبتك في حذف الطلب رقم <strong>#{order.id}</strong>؟ 
                  هذا الإجراء لا يمكن التراجع عنه.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Order Details Box */}
        {!success && order && (
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#f5f5f5",
              border: "1px solid #ddd",
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
              تفاصيل الطلب:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography variant="body2">
                <strong>رقم الطلب:</strong> #{order.id}
              </Typography>
              <Typography variant="body2">
                <strong>الحالة:</strong> {order.status}
              </Typography>
              <Typography variant="body2">
                <strong>النوع:</strong> {order.order_type || order.type}
              </Typography>
              <Typography variant="body2">
                <strong>التاريخ:</strong> {order.date}
              </Typography>
              <Typography variant="body2">
                <strong>الإجمالي:</strong> {order.total}
              </Typography>
              {orderSubTotalBeforeFees !== undefined && (
                <Typography variant="body2">
                  <strong>الإجمالي قبل الخصم والضريبة:</strong> {Number(orderSubTotalBeforeFees).toFixed(2)}
                </Typography>
              )}
              {order.customer_name && (
                <Typography variant="body2">
                  <strong>العميل:</strong> {order.customer_name}
                </Typography>
              )}
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
        >
          إلغاء
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={loading || !isDeleteAllowed}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {loading && <CircularProgress size={20} color="inherit" />}
          {loading ? "جاري الحذف..." : "حذف الطلب"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteOrderDialog;
